import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { PanelSelect } from "../../../PanelSelect";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type { UnroutedDeviceRowProps } from "./types";

export const UnroutedDeviceRow = ({
  device,
  iconDataUrl,
  isPinned,
  onPin,
  targets,
  onAdopt,
}: UnroutedDeviceRowProps) => {
  const { t } = useTranslation();

  // Nowhere to put it is not a choice to offer. A device the run published no
  // position for cannot be given a cable either — there is no point to tap to.
  const canAdopt =
    Boolean(onAdopt) && targets.length > 0 && Boolean(device.position);

  return (
    <div
      data-testid={`unrouted-device-row-${device.id}`}
      className={clsx(
        "flex items-center gap-2 rounded border-t border-violet-100 px-1 py-1.5 first:border-t-0",
        isPinned ? "bg-violet-200" : "hover:bg-violet-100",
      )}
    >
      <button
        type="button"
        data-testid={`unrouted-device-pin-${device.id}`}
        aria-pressed={isPinned}
        disabled={!device.position}
        onClick={onPin}
        className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-default"
      >
        {iconDataUrl ? (
          <img
            src={iconDataUrl}
            alt=""
            draggable={false}
            className="h-6 w-6 flex-none rounded border border-violet-100 object-cover"
          />
        ) : (
          <span
            aria-hidden
            className="grid h-6 w-6 flex-none place-items-center rounded border border-violet-100 bg-violet-50"
          >
            <span className="h-2 w-2 border-2 border-violet-400" />
          </span>
        )}
        {/* Its place in the run's own list of unplaced devices — not the number
            any circuit card shows, because this is a different list. */}
        <span className="flex-none text-[13px] font-semibold tabular-nums text-violet-500">
          {`#${device.number}`}
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] text-violet-900">
          {device.name}
        </span>
      </button>
      {canAdopt ? (
        <PanelSelect
          value=""
          options={targets}
          trigger="field"
          placeholder={t("smartSurveyor.cablesBeta.unroutedAdoptPlaceholder")}
          ariaLabel={t("smartSurveyor.cablesBeta.unroutedAdoptLabel", {
            name: device.name,
          })}
          onPick={(circuitId) => {
            trackEvent(
              ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_DEVICES_CHANGED,
              { action: "adopted", deviceCount: 1 },
            );
            onAdopt?.(circuitId);
          }}
        />
      ) : (
        <span className="flex-none text-[13px] text-violet-600">
          {t("smartSurveyor.cablesBeta.unroutedRowNoRun")}
        </span>
      )}
    </div>
  );
};
